import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AdminPageLayout, Card, StatCard, StatGrid } from '../../components';
import {
  Listing,
  ListingActionBar,
  ListingFilter,
  ListingItem
} from '../../components/listing/v2';
import {
  EditAction,
  DeleteAction
} from '../../components/listing/v2/ListingAction';
import galleryApi from '../../services/galleryApi';
import { Image, Settings, Eye, EyeOff, ImageIcon } from 'lucide-react';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useToast } from '../../contexts/ToastContext';

const GalleryManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [images, setImages] = useState([]);
  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all'
  });
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadGalleryData = useCallback(async () => {
    try {
      setLoading(true);

      // Load images
      const imagesResponse = await galleryApi.getImages(false);
      setImages(imagesResponse.data?.images || []);

      // Load settings
      const settingsResponse = await galleryApi.getSettings();
      setSettings(settingsResponse.data || {});
    } catch (err) {
      console.error('Error loading gallery data:', err);
      showError('Failed to load gallery data');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadGalleryData();
  }, [loadGalleryData]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleAddImage = () => {
    navigate('/admin/gallery/upload');
  };

  const handleEditImage = (imageId) => {
    navigate(`/admin/gallery/${imageId}/edit`);
  };

  const handleDeleteImage = (image) => {
    setShowDeleteModal(image);
  };

  const confirmDeleteImage = async () => {
    if (!showDeleteModal) return;

    setIsDeleting(true);
    try {
      await galleryApi.deleteImage(showDeleteModal.id);
      showSuccess(`Image "${showDeleteModal.title}" deleted successfully`);
      setImages(images.filter(img => img.id !== showDeleteModal.id));
      setShowDeleteModal(null);
    } catch (err) {
      console.error('Error deleting image:', err);
      showError('Failed to delete image');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleActive = async (imageId) => {
    try {
      const response = await galleryApi.toggleActive(imageId);
      const updatedImage = response.data;

      setImages(images.map(img =>
        img.id === imageId ? updatedImage : img
      ));
      showSuccess(updatedImage.active ? 'Image activated' : 'Image deactivated');
    } catch (err) {
      console.error('Error toggling image status:', err);
      showError('Failed to update image status');
    }
  };

  const handleSettingsUpdate = async (newSettings) => {
    try {
      await galleryApi.updateSettings(newSettings);
      setSettings(newSettings);
      setShowSettings(false);
      showSuccess('Settings updated successfully');
    } catch (err) {
      console.error('Error updating settings:', err);
      showError('Failed to update settings');
    }
  };

  const filteredImages = images.filter(image => {
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      if (!image.title?.toLowerCase().includes(searchTerm) &&
        !image.description?.toLowerCase().includes(searchTerm)) {
        return false;
      }
    }
    if (filters.status === 'active' && !image.active) {
      return false;
    }
    if (filters.status === 'inactive' && image.active) {
      return false;
    }
    return true;
  });

  const activeImages = images.filter(img => img.active);
  const inactiveImages = images.filter(img => !img.active);

  const filterBar = (
    <ListingFilter
      searchValue={filters.search}
      onSearchChange={(val) => handleFilterChange('search', val)}
      searchPlaceholder="Search by title or description..."
      filters={[
        {
          name: 'status',
          label: 'Status',
          options: [
            { value: 'all', label: 'All Images' },
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' }
          ]
        }
      ]}
      activeFilters={{ status: filters.status }}
      onFilterChange={(name, val) => handleFilterChange(name, val)}
      onClearFilters={() => setFilters({ search: '', status: 'all' })}
    />
  );

  const headerActions = (
    <ListingActionBar
      rightSection={
        <>
          <button
            onClick={() => setShowSettings(!showSettings)}
            style={{ padding: '8px 16px', marginRight: '8px', borderRadius: '6px', backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
            title="Carousel settings"
          >
            <Settings size={16} /> Settings
          </button>
          <button
            onClick={handleAddImage}
            style={{ padding: '8px 16px', borderRadius: '6px', backgroundColor: 'var(--color-primary)', color: 'white', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <Image size={16} /> Add Image
          </button>
        </>
      }
    />
  );

  return (
    <AdminPageLayout
      title="GALLERY MANAGEMENT"
      subtitle="Manage club gallery images and carousel settings"
      backButton={false}
      maxWidth="1400px"
    >
      {/* Statistics */}
      <StatGrid style={{ marginBottom: '24px' }}>
        <StatCard
          number={images.length}
          label="Total Images"
          icon={<Image size={24} />}
        />
        <StatCard
          number={activeImages.length}
          label="Active Images"
          icon={<Eye size={24} />}
          variant="success"
        />
        <StatCard
          number={inactiveImages.length}
          label="Inactive Images"
          icon={<EyeOff size={24} />}
          variant="warning"
        />
      </StatGrid>

      {/* Settings Panel */}
      {showSettings && settings && (
        <Card style={{ marginBottom: '24px' }}>
          <div style={{ padding: '20px' }}>
            <h2 style={{ marginTop: 0, marginBottom: '16px' }}>Carousel Settings</h2>
            <SettingsForm
              settings={settings}
              onSave={handleSettingsUpdate}
              onCancel={() => setShowSettings(false)}
            />
          </div>
        </Card>
      )}

      <Listing
        loading={loading}
        items={filteredImages}
        actions={headerActions}
        filters={filterBar}
        variant="list"
        renderItem={(image) => (
          <ListingItem
            key={image.id}
            title={image.title}
            subtitle={image.description}
            icon={<ImageIcon size={32} />}
            iconVariant={image.active ? 'primary' : 'secondary'}
            actions={
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => handleToggleActive(image.id)}
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', color: image.active ? 'var(--color-success)' : 'var(--color-warning)', background: 'none', border: 'none', padding: '0' }}
                  title={image.active ? 'Deactivate image' : 'Activate image'}
                >
                  {image.active ? <Eye size={14} /> : <EyeOff size={14} />}
                </button>
                <EditAction size="xs" onClick={() => handleEditImage(image.id)} />
                <DeleteAction size="xs" onClick={() => handleDeleteImage(image)} />
              </div>
            }
          >
            <div style={{ display: 'flex', gap: '16px', marginTop: '4px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', color: 'var(--color-text-tertiary)' }}>
                Order: #{image.display_order || '-'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', color: 'var(--color-text-tertiary)' }}>
                Created {new Date(image.created_at).toLocaleDateString()}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem' }}>
                {image.active ? (
                  <span style={{ color: 'var(--color-success)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Active
                  </span>
                ) : (
                  <span style={{ color: 'var(--color-warning)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Inactive
                  </span>
                )}
              </div>
            </div>
          </ListingItem>
        )}
        emptyMessage="No gallery images found"
      />

      {showDeleteModal && (
        <ConfirmDialog
          isOpen={true}
          title="Delete Image"
          message={`Are you sure you want to delete "${showDeleteModal.title}"?`}
          details="This action cannot be undone. The image will be permanently removed from the gallery."
          variant="danger"
          onConfirm={confirmDeleteImage}
          onClose={() => setShowDeleteModal(null)}
          confirmText="Delete Image"
          cancelText="Cancel"
          loading={isDeleting}
        />
      )}
    </AdminPageLayout>
  );
};

// Settings Form Component
const SettingsForm = ({ settings, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    display_strategy: settings.display_strategy || 'ordered',
    auto_advance: settings.auto_advance ?? true,
    carousel_interval: settings.carousel_interval || 5000,
    transition_effect: settings.transition_effect || 'slide'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form style={{ display: 'flex', flexDirection: 'column', gap: '20px' }} onSubmit={handleSubmit}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Display Strategy</label>
          <select
            value={formData.display_strategy}
            onChange={(e) => setFormData({ ...formData, display_strategy: e.target.value })}
            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--color-border)' }}
          >
            <option value="ordered">Ordered (by display order)</option>
            <option value="random">Random</option>
          </select>
          <span style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)', display: 'block', marginTop: '4px' }}>
            How images are ordered in the carousel
          </span>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Transition Effect</label>
          <select
            value={formData.transition_effect}
            onChange={(e) => setFormData({ ...formData, transition_effect: e.target.value })}
            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--color-border)' }}
          >
            <option value="slide">Slide</option>
            <option value="fade">Fade</option>
          </select>
          <span style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)', display: 'block', marginTop: '4px' }}>
            Animation effect between images
          </span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.auto_advance}
              onChange={(e) => setFormData({ ...formData, auto_advance: e.target.checked })}
            />
            <span style={{ fontWeight: '500' }}>Auto-advance carousel</span>
          </label>
          <span style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)', display: 'block', marginTop: '4px', marginLeft: '28px' }}>
            Automatically move to next image
          </span>
        </div>

        {formData.auto_advance && (
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Interval (seconds)</label>
            <input
              type="number"
              min="1"
              max="60"
              value={formData.carousel_interval / 1000}
              onChange={(e) => setFormData({
                ...formData,
                carousel_interval: parseInt(e.target.value) * 1000
              })}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--color-border)' }}
            />
            <span style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)', display: 'block', marginTop: '4px' }}>
              Time between slides (1-60 seconds)
            </span>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '12px' }}>
        <button
          type="button"
          onClick={onCancel}
          style={{ padding: '8px 16px', borderRadius: '6px', backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', cursor: 'pointer' }}
        >
          Cancel
        </button>
        <button
          type="submit"
          style={{ padding: '8px 16px', borderRadius: '6px', backgroundColor: 'var(--color-primary)', color: 'white', border: 'none', cursor: 'pointer' }}
        >
          Save Settings
        </button>
      </div>
    </form>
  );
};

export default GalleryManagement;
